import React, { useState, useRef, useEffect } from "react";
import { Form, Button } from "react-bootstrap";
import { enqueueSnackbar } from "notistack";

interface VerificationCodeInputProps {
  onVerify: (e: React.FormEvent<HTMLFormElement>, code: string) => Promise<void>;
  onBack: () => void;
  backTitle?: string;
  loading: boolean;
  timer: number;
  onResend: () => void;
}

export const VerificationCodeInput: React.FC<VerificationCodeInputProps> = ({
  onVerify,
  onBack,
  backTitle = "Назад",
  loading,
  timer,
  onResend,
}) => {
  const [code, setCode] = useState<string[]>(["", "", "", ""]);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const submitTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const enteredCode = code.join("");
    if (enteredCode.length !== 4) {
      enqueueSnackbar("Введите полный 4-значный код.", { variant: "error" });
      return;
    }

    await onVerify(e, enteredCode);
  };

  const handleCodeChange = (value: string, index: number) => {
    if (!/^\d?$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === "Backspace") {
      if (code[index]) {
        const newCode = [...code];
        newCode[index] = "";
        setCode(newCode);
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
        const newCode = [...code];
        newCode[index - 1] = "";
        setCode(newCode);
      }
    }
  };

  useEffect(() => {
    const enteredCode = code.join("");

    if (submitTimeoutRef.current) {
      clearTimeout(submitTimeoutRef.current);
      submitTimeoutRef.current = null;
    }

    if (enteredCode.length === 4 && !loading) {
      submitTimeoutRef.current = setTimeout(() => {
        if (formRef.current) {
          formRef.current.requestSubmit();
        }
      }, 500);
    }

    return () => {
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
      }
    };
  }, [code, loading]);

  return (
    <Form ref={formRef} noValidate onSubmit={handleSubmit}>
      <div className="d-flex justify-content-between mb-3">
        {code.map((digit, i) => (
          <Form.Control
            key={i}
            type="text"
            inputMode="numeric"
            value={digit}
            maxLength={1}
            onChange={(e) => handleCodeChange(e.target.value, i)}
            onKeyDown={(e) => handleKeyDown(e, i)}
            ref={(el: any) => {
              inputRefs.current[i] = el;
            }}
            className="text-center mx-1"
            style={{ width: "3rem", fontSize: "1.5rem" }}
          />
        ))}
      </div>

      {timer > 0 ? (
        <p className="text-center text-muted">
          Запросить код повторно через {timer} сек.
        </p>
      ) : (
        <Button
          variant="link"
          className="w-100 mb-2"
          style={{ color: "#6c757d" }}
          onClick={onResend}
          type="button"
        >
          Запросить код повторно
        </Button>
      )}

      <Button
        variant="link"
        style={{ color: "#6c757d" }}
        className="w-100"
        onClick={onBack}
        type="button"
      >
        {backTitle}
      </Button>
    </Form>
  );
};
